import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import client from "../api/client";
import settlementAPI from "../api/settlementAPI";
import "./Settlement.css";

const INDIAN_STATES = [
  "Andhra Pradesh",
  "Arunachal Pradesh",
  "Assam",
  "Bihar",
  "Chhattisgarh",
  "Delhi",
  "Goa",
  "Gujarat",
  "Haryana",
  "Himachal Pradesh",
  "Jharkhand",
  "Karnataka",
  "Kerala",
  "Madhya Pradesh",
  "Maharashtra",
  "Manipur",
  "Meghalaya",
  "Mizoram",
  "Nagaland",
  "Odisha",
  "Punjab",
  "Rajasthan",
  "Sikkim",
  "Tamil Nadu",
  "Telangana",
  "Tripura",
  "Uttar Pradesh",
  "Uttarakhand",
  "West Bengal",
];

const PLATFORMS = ["ALL", "DIRECT", "FLIPKART", "AMAZON", "MEESHO", "WEBSITE"];

const emptyForm = {
  product_id: "",
  platform: "ALL",
  state: "",
  base_price: "",
  settlement_percentage: "",
  expected_settlement: "",
  dto_limit: "",
  rto_value: 0,
  status: "Active",
};

const formatCurrency = (value) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value || 0));

const calculateExpectedSettlement = (basePrice, percentage) =>
  basePrice + basePrice * (percentage / 100);

const normalizeDtoLimit = (value) => {
  const num = Number(value || 0);
  if (!Number.isFinite(num) || num === 0) return 0;
  return -Math.abs(num);
};

export default function Settlement() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [rules, setRules] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const selectedProduct = useMemo(
    () => products.find((product) => String(product.id) === String(form.product_id)),
    [products, form.product_id]
  );

  const fetchProducts = async () => {
    try {
      const res = await client.get("/inventory");
      const data = res.data?.inventory || res.data?.data || res.data || [];
      setProducts(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error fetching inventory for settlement:", err);
      setProducts([]);
    }
  };

  const fetchRules = async () => {
    try {
      const data = await settlementAPI.getAll();
      setRules(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error fetching settlement rules:", err);
      setRules([]);
    }
  };

  const refreshData = async () => {
    setLoading(true);
    setError("");
    try {
      await Promise.all([fetchProducts(), fetchRules()]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshData();
  }, []);

  useEffect(() => {
    const basePrice = Number(form.base_price || 0);
    const percentage = Number(form.settlement_percentage || 0);
    const expectedSettlement = calculateExpectedSettlement(basePrice, percentage);
    setForm((prev) => ({
      ...prev,
      expected_settlement:
        prev.settlement_percentage === "" || prev.base_price === ""
          ? ""
          : expectedSettlement.toFixed(2),
    }));
  }, [form.base_price, form.settlement_percentage]);

  const handleProductChange = (productId) => {
    const product = products.find((item) => String(item.id) === String(productId));
    setForm((prev) => ({
      ...prev,
      product_id: productId,
      base_price: product ? Number(product.price || 0).toFixed(2) : "",
    }));
  };

  const validateForm = () => {
    if (!form.product_id) return "Product is required";
    if (!form.state) return "State is required";
    if (form.settlement_percentage === "") return "Settlement percentage is required";
    if (Number(form.settlement_percentage) < 0) return "Settlement percentage cannot be negative";
    if (form.dto_limit === "") return "DTO limit is required";
    return "";
  };

  const resetForm = () => {
    setForm(emptyForm);
    setEditingId(null);
    setError("");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    const payload = {
      product_id: Number(form.product_id),
      platform: form.platform,
      state: form.state,
      settlement_percentage: Number(form.settlement_percentage),
      dto_limit: normalizeDtoLimit(form.dto_limit),
      status: form.status,
    };

    setSaving(true);
    setError("");
    try {
      if (editingId) {
        await settlementAPI.update(editingId, payload);
      } else {
        await settlementAPI.create(payload);
      }
      await fetchRules();
      resetForm();
      alert(editingId ? "Settlement rule updated successfully" : "Settlement rule saved successfully");
    } catch (err) {
      console.error("Error saving settlement rule:", err);
      setError(err?.response?.data?.message || "Failed to save settlement rule");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (rule) => {
    setEditingId(rule.id);
    setForm({
      product_id: String(rule.product_id || ""),
      platform: rule.platform || "ALL",
      state: rule.state || "",
      base_price: Number(rule.base_price || 0).toFixed(2),
      settlement_percentage: String(rule.settlement_percentage ?? ""),
      expected_settlement: Number(rule.expected_settlement || 0).toFixed(2),
      dto_limit: String(rule.dto_limit ?? ""),
      rto_value: 0,
      status: rule.status || "Active",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (rule) => {
    if (!window.confirm(`Delete settlement rule for ${rule.product_name || "this product"} in ${rule.state}?`)) {
      return;
    }

    try {
      await settlementAPI.delete(rule.id);
      await fetchRules();
      if (editingId === rule.id) resetForm();
      alert("Settlement rule deleted successfully");
    } catch (err) {
      console.error("Error deleting settlement rule:", err);
      alert(err?.response?.data?.message || "Failed to delete settlement rule");
    }
  };

  return (
    <div className="page-container settlement-page">
      <div className="settlement-header">
        <div>
          <h2>Settlement</h2>
          <p>Manage product and state wise settlement rate cards.</p>
        </div>
        <div className="settlement-header-actions">
          <button
            type="button"
            onClick={() => navigate("/inventory")}
            className="settlement-back-btn"
          >
            Back to Inventory
          </button>
          <button
            type="button"
            onClick={refreshData}
            className="settlement-refresh-btn"
          >
            {loading ? "Refreshing..." : "Refresh"}
          </button>
        </div>
      </div>

      <div className="settlement-summary-grid">
        <div className="settlement-summary-card">
          <span>Total Rules</span>
          <strong>{rules.length}</strong>
        </div>
        <div className="settlement-summary-card">
          <span>Active Rules</span>
          <strong>{rules.filter((rule) => rule.status === "Active").length}</strong>
        </div>
        <div className="settlement-summary-card">
          <span>Products Loaded</span>
          <strong>{products.length}</strong>
        </div>
      </div>

      <div className="settlement-card">
        <div className="settlement-card-header">
          <div>
            <h3>{editingId ? "Edit Settlement Rule" : "Create Settlement Rule"}</h3>
            <p>Base price and expected settlement are calculated from inventory price.</p>
          </div>
          <span className="settlement-card-chip">Rate Card</span>
        </div>

        <form onSubmit={handleSubmit} className="settlement-form">
          <label className="settlement-field">
            <span>Product</span>
            <select
              value={form.product_id}
              onChange={(event) => handleProductChange(event.target.value)}
              required
            >
              <option value="">Select Product</option>
              {products.map((product) => (
                <option key={product.id} value={product.id}>
                  {product.name}
                </option>
              ))}
            </select>
          </label>

          <label className="settlement-field">
            <span>Platform</span>
            <select
              value={form.platform}
              onChange={(event) => setForm((prev) => ({ ...prev, platform: event.target.value }))}
            >
              {PLATFORMS.map((platform) => (
                <option key={platform} value={platform}>
                  {platform === "ALL" ? "All Platforms" : platform}
                </option>
              ))}
            </select>
          </label>

          <label className="settlement-field">
            <span>State</span>
            <select
              value={form.state}
              onChange={(event) => setForm((prev) => ({ ...prev, state: event.target.value }))}
              required
            >
              <option value="">Select State</option>
              {INDIAN_STATES.map((state) => (
                <option key={state} value={state}>
                  {state}
                </option>
              ))}
            </select>
          </label>

          <label className="settlement-field">
            <span>Base Price</span>
            <input
              value={form.base_price}
              readOnly
              placeholder="Auto-filled"
            />
          </label>

          <label className="settlement-field">
            <span>Settlement Percentage (%)</span>
            <input
              type="number"
              min="0"
              step="0.01"
              value={form.settlement_percentage}
              onChange={(event) =>
                setForm((prev) => ({ ...prev, settlement_percentage: event.target.value }))
              }
              required
            />
          </label>

          <label className="settlement-field">
            <span>Expected Settlement</span>
            <input
              value={form.expected_settlement}
              readOnly
              placeholder="Auto-calculated"
            />
          </label>

          <label className="settlement-field">
            <span>DTO Limit</span>
            <input
              type="number"
              step="0.01"
              value={form.dto_limit}
              onChange={(event) => setForm((prev) => ({ ...prev, dto_limit: event.target.value }))}
              onBlur={(event) =>
                setForm((prev) => ({
                  ...prev,
                  dto_limit:
                    event.target.value === ""
                      ? ""
                      : String(normalizeDtoLimit(event.target.value)),
                }))
              }
              placeholder="0 or negative value"
              required
            />
          </label>

          <label className="settlement-field">
            <span>RTO Value</span>
            <input
              value={form.rto_value}
              readOnly
            />
          </label>

          <label className="settlement-field">
            <span>Status</span>
            <select
              value={form.status}
              onChange={(event) => setForm((prev) => ({ ...prev, status: event.target.value }))}
            >
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>
          </label>

          {error && (
            <div className="settlement-error">
              {error}
            </div>
          )}

          <div className="settlement-form-actions">
            <button
              type="submit"
              disabled={saving}
              className="settlement-primary-btn"
            >
              {saving ? "Saving..." : editingId ? "Update Settlement" : "Add Settlement"}
            </button>
            {editingId && (
              <button
                type="button"
                onClick={resetForm}
                className="settlement-secondary-btn"
              >
                Cancel Edit
              </button>
            )}
          </div>
        </form>

        {selectedProduct && (
          <div className="settlement-selected-product">
            Selected product: <strong>{selectedProduct.name}</strong>
          </div>
        )}
      </div>

      <div className="settlement-card settlement-table-card">
        <div className="settlement-card-header">
          <div>
            <h3>Settlement Rules</h3>
            <p>All saved rate card rules by product and state.</p>
          </div>
          {loading ? (
            <span className="settlement-loading">Loading...</span>
          ) : (
            <span className="settlement-card-chip">{rules.length} Records</span>
          )}
        </div>
        <div className="settlement-table-wrap">
          <table className="settlement-table">
            <thead>
              <tr>
                <th>Product Name</th>
                <th>Platform</th>
                <th>State</th>
                <th>Base Price</th>
                <th>Settlement %</th>
                <th>Expected Settlement</th>
                <th>DTO Limit</th>
                <th>RTO Value</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rules.length === 0 ? (
                <tr>
                  <td colSpan="10" className="settlement-empty">
                    No settlement rules found
                  </td>
                </tr>
              ) : (
                rules.map((rule) => (
                  <tr key={rule.id}>
                    <td className="settlement-product-cell">{rule.product_name || "-"}</td>
                    <td>{rule.platform === "ALL" ? "All Platforms" : rule.platform || "ALL"}</td>
                    <td>{rule.state}</td>
                    <td>{formatCurrency(rule.base_price)}</td>
                    <td>{Number(rule.settlement_percentage || 0)}%</td>
                    <td>{formatCurrency(rule.expected_settlement)}</td>
                    <td>{Number(rule.dto_limit || 0)}</td>
                    <td>{Number(rule.rto_value || 0)}</td>
                    <td>
                      <span
                        className={`settlement-status ${
                          rule.status === "Active" ? "settlement-status-active" : "settlement-status-inactive"
                        }`}
                      >
                        {rule.status}
                      </span>
                    </td>
                    <td>
                      <div className="settlement-row-actions">
                        <button
                          type="button"
                          onClick={() => handleEdit(rule)}
                          className="settlement-edit-btn"
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(rule)}
                          className="settlement-delete-btn"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
